<script setup lang="ts">
import type {Media} from '~/types/legacy'

const {
  public: {baseUrl},
} = useRuntimeConfig()

const {fetch} = useBrand()

const {data: initialData} = await useAsyncData('brand', () => fetch())
const {data: brand} = useLivePreview({initialData: initialData.value, depth: 2})

if (!brand.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

const meta = brand.value?.meta

useSeoMeta({
  title: meta?.title || 'Brand Guidelines | Webamboos',
  description: meta?.description,
  ogUrl: `${baseUrl}/brand`,
  ogTitle: meta?.ogTitle || meta?.title,
  ogDescription: meta?.ogDescription || meta?.description,
  ogImage: resolveMediaUrl(meta?.image),
  ogImageAlt: resolveMediaAlt(meta?.image),
})

const textColors = {
  white: 'text-white',
  dark: 'text-gray-900',
} as const
</script>

<template>
  <Container v-if="brand" class="screen-content flex-1 px-0 pb-32">
    <LayoutPageHeader :title="$t('header.page_titles.brand')" />
    <div class="mx-auto max-w-xl px-8">
      <div class="prose !max-w-full prose-h2:text-lg prose-h2:!font-bold prose-p:text-gray-800 prose-a:no-underline">
        <!-- Download Assets Button -->
        <div v-if="(brand?.assetsFile as Media)?.url" class="flex items-center justify-center">
          <a
            :href="(brand.assetsFile as Media).url ?? undefined"
            download
            class="bg-gradient-to-br from-primary-darkish to-primary-normal bg-clip-text text-lg font-medium text-transparent"
          >
            Download Assets
          </a>
        </div>

        <!-- Name Guidelines -->
        <template v-if="brand?.nameGuidelines">
          <h2>Name</h2>
          <RichText :raw="brand.nameGuidelines" />
        </template>

        <!-- Usage Guidelines -->
        <template v-if="brand?.usageGuidelines">
          <h2>Using the logo</h2>
          <RichText :raw="brand.usageGuidelines" />
        </template>

        <!-- Logomarks -->
        <template v-if="brand?.logomarks?.length">
          <h2>Logomark</h2>
          <RichText v-if="brand?.logomarksDescription" :raw="brand.logomarksDescription" />
          <div class="not-prose space-y-4">
            <BrandLogoCard
              v-for="(logo, index) in brand.logomarks"
              :key="logo.id ?? index"
              :image="logo.image"
              :variant="logo.variant"
            />
          </div>
        </template>

        <!-- Logos -->
        <template v-if="brand?.logos?.length">
          <h2>Logo</h2>
          <RichText v-if="brand?.logosDescription" :raw="brand.logosDescription" />
          <div class="not-prose flex space-x-4">
            <BrandLogoCard
              v-for="(logo, index) in brand.logos"
              :key="logo.id ?? index"
              :image="logo.image"
              :variant="logo.variant"
              class="max-h-40 w-full"
            />
          </div>
        </template>

        <!-- Colors -->
        <template v-if="brand?.colors?.length">
          <h2>Colors</h2>
          <RichText v-if="brand?.colorGuidelines" :raw="brand.colorGuidelines" />
          <div class="not-prose flex space-x-4">
            <div
              v-for="(color, index) in brand.colors"
              :key="color.id ?? index"
              class="w-1/2 rounded-md p-4 text-center font-bold tabular-nums shadow"
              :style="{backgroundColor: color.hex}"
              :class="textColors[color.textColor as keyof typeof textColors]"
            >
              {{ color.hex }}
            </div>
          </div>
        </template>
      </div>
    </div>
  </Container>
</template>
